import { useClickAway } from 'hooks/useClickAway';
import { FC, useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

type DropdownAlign = 'left' | 'right';
type AppendToDirection = 'top' | 'bottom';

interface DropdownProps {
  children: React.ReactNode;
  opener: React.ReactNode;
  top?: number | string;
  bottom?: number | string;
  left?: number | string;
  right?: number | string;
  openerEmpty?: boolean;
  byClick?: boolean;
  closeOnClickDate?: Date;
  appendToBody?: boolean;
  appendToDirection?: AppendToDirection;
  align?: DropdownAlign;
  dropdownClassName?: string;
}

interface Position {
  top: number;
  left: number | 'auto';
  right: number | 'auto';
}

const Dropdown: FC<DropdownProps> = (props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0, right: 'auto' });
  const openerRef = useRef<HTMLSpanElement>(null);
  const clickAwayRef = useClickAway(() => setIsOpen(false));

  useEffect(() => {
    if (props.closeOnClickDate) {
      setIsOpen(false);
    }
  }, [props.closeOnClickDate]);

  useEffect(() => {
    if (isOpen && props.appendToBody) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, props.appendToBody]); //eslint-disable-line

  const updatePosition = () => {
    if (openerRef.current) {
      const rect = openerRef.current.getBoundingClientRect();
      const appendToDirection = props.appendToDirection || 'bottom';

      const left = props.align === 'right' ? 'auto' : rect.left + window.scrollX;
      const right =
        props.align === 'right' ? window.innerWidth - rect.right + window.scrollX : 'auto';

      setPosition({
        top:
          appendToDirection === 'top'
            ? rect.top + window.scrollY - 5
            : rect.bottom + window.scrollY + 5,
        left,
        right,
      });
    }
  };

  const handleClick = () => {
    if (props.byClick) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={'c-dropdown' + (props.openerEmpty ? ' c-dropdown-empty' : '')}>
      <span
        ref={openerRef}
        className={'c-dropdown-opener'}
        onMouseEnter={() => !props.byClick && setIsOpen(true)}
        onMouseLeave={() => !props.byClick && setIsOpen(false)}
      >
        <span onClick={handleClick}>{props.opener}</span>
        {isOpen && !props.appendToBody && (
          <div
            ref={clickAwayRef}
            className={"c-dropdown-content" + (props.dropdownClassName ? ` ${props.dropdownClassName}` : '')}
            style={{ top: props.top, bottom: props.bottom, left: props.left, right: props.right }}
          >
            {props.children}
          </div>
        )}
        {props.appendToBody &&
          isOpen &&
          createPortal(
            <div
              ref={clickAwayRef}
              className={"c-dropdown-content" + (props.dropdownClassName ? ` ${props.dropdownClassName}` : '')}
              style={{
                position: 'absolute',
                top: `${position.top}px`,
                left: position.left === 'auto' ? 'auto' : `${position.left}px`,
                right: position.right === 'auto' ? 'auto' : `${position.right}px`,
                transform: props.appendToDirection === 'top' ? 'translateY(-100%)' : undefined,
                zIndex: 1000,
              }}
            >
              {props.children}
            </div>,
            document.body
          )}
      </span>
    </div>
  );
};

export default Dropdown;
